import Image from 'next/image';
import { forwardRef } from 'react';

function positiveDimension(...values) {
  for (const value of values) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return Math.round(numericValue);
    }
  }
  return null;
}

export function normalizeDatabaseImageSource(image) {
  const rawSource = typeof image === 'string'
    ? image
    : image?.url || image?.src || image?.file_name || '';
  const source = String(rawSource || '').trim().replace(/\\/g, '/');

  if (!source) return '';
  if (/^(?:https?:|blob:|data:)/i.test(source)) return source;
  return source.startsWith('/') ? source : `/${source.replace(/^\.\//, '')}`;
}

const DatabaseImage = forwardRef(function DatabaseImage({
  image,
  alt,
  className,
  loading = 'lazy',
  sizes = '100vw',
  onLoad,
  ...nativeProps
}, forwardedRef) {
  const src = normalizeDatabaseImageSource(image);
  if (!src) return null;

  const width = positiveDimension(image?.width, image?.image_width, image?.natural_width);
  const height = positiveDimension(image?.height, image?.image_height, image?.natural_height);
  const canOptimize = src.startsWith('/') && width && height;

  if (canOptimize) {
    return (
      <Image
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        sizes={sizes}
        loading={loading}
        onLoad={onLoad}
        ref={forwardedRef}
        {...nativeProps}
      />
    );
  }

  return (
    // Database and user media without trustworthy intrinsic dimensions must retain native sizing.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      className={className}
      loading={loading}
      onLoad={onLoad}
      ref={forwardedRef}
      {...nativeProps}
    />
  );
});

DatabaseImage.displayName = 'DatabaseImage';

export default DatabaseImage;
